import { useMemo } from 'react';

export type GPUTier = 'high' | 'mid' | 'low';

/**
 * useGPUTier
 *
 * Cheap, synchronous tier detection. Heuristic only — used to seed quality
 * presets at boot (see docs/FRONTEND_PLAN.md § Performance).
 *
 * Signals:
 *   1. Presence of navigator.gpu                — strong "high" indicator.
 *   2. WEBGL_debug_renderer_info renderer name  — coarse vendor heuristic.
 *   3. devicePixelRatio                         — display density penalty.
 */
export function useGPUTier(): GPUTier {
  return useMemo(() => detect(), []);
}

function detect(): GPUTier {
  if (typeof window === 'undefined') return 'mid';

  const hasWebGPU = typeof (navigator as Navigator & { gpu?: unknown }).gpu !== 'undefined';
  const dpr = window.devicePixelRatio || 1;
  const renderer = readWebGLRenderer();

  const score =
    (hasWebGPU ? 2 : 0) +
    scoreRenderer(renderer) -
    (dpr >= 2.5 ? 1 : 0);

  if (score >= 3) return 'high';
  if (score >= 1) return 'mid';
  return 'low';
}

function readWebGLRenderer(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (!gl) return '';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return '';
    const value = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL);
    return typeof value === 'string' ? value : '';
  } catch {
    return '';
  }
}

function scoreRenderer(renderer: string): number {
  const r = renderer.toLowerCase();
  if (!r) return 1;
  if (/(rtx|radeon rx|m1|m2|m3|m4|apple gpu|arc a|geforce gtx 1[6789])/.test(r)) return 2;
  if (/(intel|uhd|hd graphics|mali|adreno 6|powervr)/.test(r)) return 0;
  return 1;
}
